import { Button, ButtonMode, Divider, Icon, ICONS } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES, USER_ROUTES } from '../../Base.constants';
import useGetUserDropdownOptions from '../../hooks/header/useGetUserDropdownOptions';
import useFetchSavedSearches from '../../hooks/searches/useFetchSavedSearches';
import useInitBroadcastChannels from '../../hooks/useInitBroadcastChannels';
import styles from './Header.module.scss';
import HeaderDropdown from './HeaderButton';
import InitialsIcon from './HeaderButton/InitialsIcon';
import SearchBar from './SearchBar';
import MyContentWizard from './Wizard/MyContentWizard';
import useGetMyContentDropdownOptions from '../../hooks/header/useGetMyContentDropdownOptions';
import useFetchUserProfile from '../../hooks/profile/useFetchUserProfile';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';

const cx = classNames.bind(styles);

export interface HeaderProps {
  isFirstLogin: boolean | undefined;
  onOpenRequestResearch?: () => void;
}

const Header = ({ isFirstLogin = false, onOpenRequestResearch }: HeaderProps): React.ReactElement => {
  const [isMyContentDropdownOpen, setIsMyContentDropdownOpen] = useState<boolean>(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState<boolean>(false);
  const [isCustomDropdownOptionShown, setIsCustomDropdownOptionShown] = useState<boolean>(isFirstLogin);
  // Need to pre-fetch data for search bar, so it doesn't show loader on first focus
  useFetchSavedSearches();
  useInitBroadcastChannels();
  const { userDropdownOptions } = useGetUserDropdownOptions();
  const { myContentOptions } = useGetMyContentDropdownOptions();

  const { data: profile } = useFetchUserProfile();
  const { data: userPermissions } = useFetchUserPermissions();

  useEffect(() => {
    setIsCustomDropdownOptionShown(isFirstLogin);
    setIsMyContentDropdownOpen(isFirstLogin);
  }, [isFirstLogin]);

  return (
    <div className={cx('body')} data-no-translate={1} data-testid="header">
      <div className={cx('container')}>
        <div className={cx('item', 'side', 'left')}>
          <Link className={cx('logo')} to={ROUTES.base} data-testid="header-logo-link">
            <Icon name={ICONS.WhiteLogo} width={200} height={45} />
          </Link>
        </div>
        <div className={cx('item', 'center')}>
          <SearchBar />
        </div>
        <div className={cx('item', 'side')}>
          {onOpenRequestResearch ? (
            <Button
              className={cx('requestBtn', { disabled: !profile?.active || !userPermissions })}
              mode={ButtonMode.PRIMARY}
              onClick={onOpenRequestResearch}
            >
              Request CR Research
            </Button>
          ) : null}
          <HeaderDropdown
            disabled={!profile?.active || !userPermissions}
            dataTestId="my-content-dropdown"
            options={myContentOptions}
            trigger={{ dataTestId: 'my-content-button', label: 'My Content', icon: <Icon name={ICONS.Bookmark} /> }}
            customOption={
              isCustomDropdownOptionShown ? (
                <MyContentWizard
                  onClick={() => {
                    setIsCustomDropdownOptionShown(false);
                    setIsMyContentDropdownOpen(false);
                  }}
                />
              ) : undefined
            }
            activeRoute={ROUTES.myContent}
            open={isMyContentDropdownOpen}
            onOpen={() => setIsMyContentDropdownOpen(true)}
            onClose={
              isCustomDropdownOptionShown
                ? () => setIsCustomDropdownOptionShown(false)
                : () => setIsMyContentDropdownOpen(false)
            }
          />
          <Divider vertical className={cx('divider')} />
          <HeaderDropdown
            dataTestId="user-dropdown"
            options={userDropdownOptions}
            trigger={{ label: 'User', icon: <InitialsIcon isOpen={isUserDropdownOpen} /> }}
            activeRoute={ROUTES.user}
            excludeSubActiveRoute={USER_ROUTES.onboarding}
            open={isUserDropdownOpen}
            small
            onOpen={() => {
              setIsCustomDropdownOptionShown(false);
              setIsMyContentDropdownOpen(false);
              setIsUserDropdownOpen(true);
            }}
            onClose={() => setIsUserDropdownOpen(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default Header;
